import { Component, Input } from '@angular/core';
import { user } from '@angular/fire/auth';
import {
  Collection,
  CollectionDetailsType,
  EContentPrivacy,
  FindCollectionStrategyEnum,
  Game,
  GameModeEnum,
  GameResults,
  IFindCollectionFilter,
  Lesson,
  PublishStatusEnum,
} from 'lingo2-models';
import { OnUiButtonState } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';
import { BaseContentCard, IContentCardState } from '../base-content-card';

/*
 */
const COLLECTION_DETAILS: CollectionDetailsType[] = [
  'id',
  'slug',
  'subject',
  'title',
  'excerpt',
  'type',
  'category',
  'multi_cover:sm',
  'changed_at',
  'author:sm',
  'author_id',
  'discount',
  'price_tiers',
  'access',
  'stats',
  'visit_info',
  'description',
  'language_id',
  'subject_id',
  'valid',
  'moderation_status',
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: null,
  totalPages: null,
};

/*
 */
interface ILessonCardComponentState extends IContentCardState {
  play: boolean;
  gameMode: GameModeEnum;
  viewProcessed: boolean;
  playButton: OnUiButtonState;
  replayAlert: boolean;
}

/*
 */
@Component({
  selector: 'app-lesson-card',
  templateUrl: './lesson-card.component.html',
  styleUrls: ['./lesson-card.component.scss'],
})
export class LessonCardComponent extends BaseContentCard {
  @Input() set item(item: Lesson) {
    this._item = Lesson.createContent(item);
    this.prepareLessonCollections();
    this.init();
  }
  get item(): Lesson {
    return this._item;
  }
  get game(): Game {
    return this._game;
  }

  /*
   */
  public state: ILessonCardComponentState = {
    ...super.getState(),
    play: false,
    gameMode: null,
    viewProcessed: false,
    playButton: 'default',
    replayAlert: false,
  };
  public passGrade: string;
  public passDate: Date;
  public isCutable: boolean;
  public replayGameId: string = '';

  protected _item: Lesson;
  protected _game: Game;
  protected _replayGame: Game;

  public lessonInCollections: Collection[];

  public prepareLessonCollections() {
    const _filter: Partial<IFindCollectionFilter> = {
      use: FindCollectionStrategyEnum.library,
      by_content_id: [this._item.id],
      publish_status: [PublishStatusEnum.published],
    };
    this.collectionsService
      .findCollections(_filter, DEFAULT_PAGINATION, COLLECTION_DETAILS)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (collections) => {
          this.lessonInCollections = collections.results;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  /*
   */
  public get isPrivate() {
    return (
      this.item.privacy === EContentPrivacy.private &&
      !(this.lessonInCollections && this.lessonInCollections[0]?.visit_info?.licensed) &&
      !this.fromCollection &&
      !this.isAuthor
    );
  }

  /*
   */
  public get isAuthor(): boolean {
    return this.item?.visit_info?.is_author;
  }

  /*
   */
  protected init() {
    super.init();
    this.passGrade =
      this.item && this.item.visit_info && this.item.visit_info.is_passed && this.item.visit_info.pass_percent
        ? GameResults.getGradeABC(this.item.visit_info.pass_percent)
        : null;
    this.passDate =
      this.item && this.item.visit_info && this.item.visit_info.is_passed && this.item.visit_info.passed_at
        ? new Date(this.item.visit_info.passed_at)
        : null;
    this.isCutable = this.item && (this.item.description || '').length > this.cutMaxLength;
    this.state.cut = this.isCutable;
    this.detectChanges();
  }

  /*
   */

  protected resetButtonState(timeout = 1500) {
    this.setTimeout(() => {
      this.state.playButton = 'default';
      this.detectChanges();
    }, timeout);
  }

  /*
   */
  public playLesson() {
    this.state.playButton = 'progress';
    this.detectChanges();
    this.state.replayAlert = true;
    this.detectChanges();
    if (!this.passGrade || !this.passDate) {
      return this.playLessonAgain();
    }
  }

  /*
   */
  public playLessonAgain() {
    this.state.replayAlert = false;
    this.detectChanges();
    this.state.gameMode = GameModeEnum.single;
    this.state.play = true;
    this.replayGameId = null;
    this._replayGame = null;
    this.detectChanges();
    if (this.deviceService.isMobile()) {
      this.state.playButton = 'default';
      this.detectChanges();
      this.resetButtonState();
    }
    this.setupMeta();
  }

  /*
   */
  public showLastGame() {
    // запросить конкретно эту игру из БД
    this.gameService
      .getGameById(this.item.visit_info.pass_game_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (game) => {
          this._replayGame = game;
          this.replayGameId = game.id;
          this.state.replayAlert = false;
          this._game = this._replayGame;
          this.state.gameMode = this._replayGame?.options?.mode;
          this.state.play = true;
          this.detectChanges();
          if (this.deviceService.isMobile()) {
            this.state.playButton = 'done';
            this.resetButtonState();
          }
        },
        error: (error: any) => {
          this._replayGame = null;
          this.replayGameId = null;
          this.state.replayAlert = false;
          this.resetButtonState();
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          return this.playLessonAgain();
        },
      });
  }

  /*
   */
  public gameLoaded() {
    this.state.playButton = 'done';
  }

  /*
   */
  public stopPlay() {
    if (this.playAutomatically) {
      this.close.emit(true);
    }
    this.state.play = false;
    this.state.playButton = 'done';
    this.resetMeta();
    this.detectChanges();

    this.contentService
      .getById(this._item.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item) => {
          this.item = item as Lesson;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected readonly user = user;
}
